// Needs the serviceDate the v2 endpoint did not: a slot recurs across dates.
import chunk from 'lodash/chunk'

import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'
import { collectRouteSlotIds } from '../slotPayloads'

import type { SlotRow } from '../slotPayloads'
import type { StepResult } from '../types'
import type { RouteId, ServiceDate } from 'types/entities/shared/ids'
import type { Deps, PipelineStep } from 'types/modules/routing'

const BATCH_SIZE = 200

export interface UnassignDailySlotStepArgs {
  readonly routeId: RouteId
  readonly serviceDate: ServiceDate
  readonly rows: ReadonlyArray<SlotRow>
}

export type UnassignDailySlotStepValue = {
  readonly stopIds: ReadonlyArray<string>
  readonly batchCount: number
}

export type UnassignDailySlotStepResult = StepResult<'UNASSIGN_DAILY', UnassignDailySlotStepValue>

export const makeUnassignDailySlotStep = (
  args: UnassignDailySlotStepArgs,
  deps: Deps
): PipelineStep<'UNASSIGN_DAILY', UnassignDailySlotStepResult> => {
  const stepId = `unassign-daily-slot:${args.routeId}:${args.serviceDate}`
  return {
    id: stepId,
    phase: 'UNASSIGN_DAILY',
    label: 'Failed to unassign stops from daily schedule',
    applyOrder: 100,
    run: async ctx => {
      if (!deps.unassignDailySlot) {
        return err({ kind: 'PRECONDITION_FAILED', reason: 'unassignDailySlot dep not provided — the v3-slot strategy requires it' })
      }
      const routeSlotIds = collectRouteSlotIds(args.rows)
      if (routeSlotIds.length === 0) {
        return ok({ phase: 'UNASSIGN_DAILY', stepId, value: { stopIds: [], batchCount: 0 } })
      }

      const batches = chunk(routeSlotIds as ReadonlyArray<string>, BATCH_SIZE)
      const collected: string[] = []
      try {
        // eslint-disable-next-line no-restricted-syntax -- batches apply in order; an array method would fan them out
        for (const batch of batches) {
          // eslint-disable-next-line no-await-in-loop -- sequential by design, see above
          const response = await deps
            .unassignDailySlot({
              routeId: args.routeId,
              data: { serviceDate: args.serviceDate, stops: [...batch] },
              skipInvalidate: true,
            })
            .unwrap()
          if (response?.stopIds) collected.push(...response.stopIds)
          ctx.recordCommit()
        }
        return ok({ phase: 'UNASSIGN_DAILY', stepId, value: { stopIds: collected, batchCount: batches.length } })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({ kind: 'BE_REJECTED', phase: 'UNASSIGN_DAILY', status, message })
      }
    },
  }
}
